import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import HeroBanner from "../components/HeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [config, setConfig] = useState(null);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/config`).then(res => res.json()),
      fetch(`${API_URL}/api/cities`).then(res => res.json())
    ])
      .then(([configData, citiesData]) => {
        setConfig(configData);
        setCities(citiesData);
      })
      .catch(console.error);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        toast.success("Message envoyé avec succès !");
        setFormData({ name: "", phone: "", email: "", message: "" });
      } else {
        toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } catch (error) {
      toast.error("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <HeroBanner
        title="Contactez Fichet Dépannage"
        subtitle="Besoin d'un serrurier Fichet ? Appelez-nous directement ou envoyez-nous un message. Nous vous répondons rapidement."
        highlightText="Fichet"
        showFeatures={false}
        showCTA={true}
        config={config}
        variant="compact"
        breadcrumb={
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Accueil
          </Link>
        }
      />

      {/* Contact Info + Form */}
      <section className="section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#0f2744] mb-6">
                Nos coordonnées
              </h2>

              <div className="space-y-6">
                {/* Phone */}
                {config && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0f2744] mb-1">Téléphone</h3>
                      <a 
                        href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                        className="text-lg font-bold text-amber-600 hover:text-amber-700 transition-colors"
                        data-testid="contact-phone-link"
                      >
                        {config.phone}
                      </a>
                      <p className="text-sm text-gray-500 mt-1">Disponible 7j/7 - 24h/24</p>
                    </div>
                  </div>
                )}

                {/* Email */}
                {config && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0f2744] mb-1">Email</h3>
                      <a 
                        href={`mailto:${config.email}`}
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        {config.email}
                      </a>
                    </div>
                  </div>
                )}

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f2744] mb-1">Horaires</h3>
                    <p className="text-gray-600">7 jours sur 7</p>
                    <p className="text-gray-600">24 heures sur 24</p>
                    <p className="text-sm text-gray-500 mt-1">Urgences incluses</p>
                  </div>
                </div>

                {/* Zone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f2744] mb-1">Zone d'intervention</h3>
                    <p className="text-gray-600">Paris et Île-de-France</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {cities.slice(0, 5).map((city) => (
                        <span 
                          key={city.id}
                          className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600"
                        >
                          {city.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency notice */}
              <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-xl">
                <h3 className="font-semibold text-red-800 mb-2">
                  ⚠️ Urgence ?
                </h3>
                <p className="text-red-700 text-sm mb-3">
                  Pour une intervention immédiate, appelez-nous directement.
                </p>
                {config && (
                  <a
                    href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                    className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                    data-testid="contact-emergency-phone"
                  >
                    <Phone className="w-4 h-4" />
                    {config.phone}
                  </a>
                )}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {success ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-green-800 mb-2">
                    Message envoyé !
                  </h3>
                  <p className="text-green-700 mb-4">
                    Nous avons bien reçu votre message et vous répondrons dans les plus brefs délais.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="text-green-600 font-medium hover:underline"
                  >
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-[#0f2744] mb-2">
                    Envoyez-nous un message
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Décrivez votre problème et nous vous recontacterons rapidement avec un devis.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nom complet *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Votre nom"
                          required
                          className="w-full"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Téléphone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="06 12 34 56 78"
                          required
                          className="w-full"
                          data-testid="contact-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="votre@email.com"
                        className="w-full"
                        data-testid="contact-email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Votre message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez votre problème de serrurerie (type de serrure, situation, urgence...)"
                        rows={5}
                        required
                        className="w-full resize-none"
                        data-testid="contact-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-cta w-full flex items-center justify-center gap-2"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Envoyer le message
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps - Zone d'intervention Île-de-France */}
      <section className="section-alt">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-[#0f2744] text-center mb-2">
            Zone d'intervention
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Nous intervenons rapidement dans Paris et toute l'Île-de-France
          </p>
          
          {/* Google Maps Embed - Centered on Paris with IDF view */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d167998.10828981803!2d2.206884134375!3d48.858859699999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e1f06e2b70f%3A0x40b82c3688c9460!2sParis%2C%20France!5e0!3m2!1sfr!2sfr!4v1706800000000!5m2!1sfr!2sfr"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Zone d'intervention Fichet Dépannage - Île-de-France"
              className="w-full"
              data-testid="google-maps-iframe"
            ></iframe>
          </div>
          
          {/* Department badges */}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {['Paris 75', 'Hauts-de-Seine 92', 'Seine-Saint-Denis 93', 'Val-de-Marne 94', 'Yvelines 78', 'Val-d\'Oise 95', 'Essonne 91', 'Seine-et-Marne 77'].map((dept) => (
              <span 
                key={dept}
                className="px-4 py-2 bg-[#0f2744] text-white rounded-full text-sm font-medium"
              >
                {dept}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-[#0f2744] to-[#1a3a5c] py-12 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Intervention d'urgence ?
          </h2>
          <p className="text-gray-300 mb-6">
            Appelez-nous maintenant, un technicien peut être chez vous en 30 minutes.
          </p>
          {config && (
            <a
              href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
              className="btn-cta text-xl"
              data-testid="contact-final-cta"
            >
              <Phone className="w-6 h-6 mr-2" />
              {config.phone}
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
